import React, { useState, useEffect, Suspense } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FaHome, FaInfoCircle, FaDollarSign, FaTachometerAlt, FaBell, FaBars, FaTimes, FaUserCircle, FaUserPlus, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext.js';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../../components/LanguageSelector.js';
import coinsIcon from '../../coins.png';
import './Navbar.css';

const Navbar = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [developerMode, setDeveloperMode] = useState(false);
  const navigate = useNavigate();

  // Debug: Check user state and authentication
  useEffect(() => {
    console.log('Navbar - User state:', user);
    console.log('Navbar - localStorage token:', localStorage.getItem('token'));
    console.log('Navbar - localStorage user:', localStorage.getItem('user'));
  }, [user]);

  // Detect mobile devices
  useEffect(() => {
    const checkIsMobile = () => {
      const width = window.innerWidth;
      console.log('Navbar - window.innerWidth:', width);
      setIsMobile(width <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Temporary debug display of user state in navbar
  // const userStatusText = user ? `User logged in: ${user.email || user.name || 'unknown'}` : 'No user logged in';

  const handleNotificationClick = () => {
    if (!("Notification" in window)) {
      alert("This browser does not support desktop notification");
    } else if (Notification.permission === "granted") {
      alert("Notification permission already granted.");
      new Notification("⭐ RapidMind Notification", {
        body: "This is a notification from RapidMind.",
        icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/18/Five-pointed_star.svg/1200px-Five-pointed_star.svg.png"
      });
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then(permission => {
        if (permission === "granted") {
          alert("%cThank you for enabling notifications!", "color: yellow; font-weight: bold;");
          new Notification("⭐ RapidMind Notification", {
            body: "Thank you for enabling notifications!",
            icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/18/Five-pointed_star.svg/1200px-Five-pointed_star.svg.png"
          });
        } else {
          alert("Notification permission denied.");
        }
      });
    } else {
      alert("Notification permission denied.");
    }
    // Close all navbar menus/icons on click
    setMenuOpen(false);
    setLangMenuOpen(false);
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const toggleLangMenu = () => {
    setLangMenuOpen(!langMenuOpen);
  };

  // Close menus on icon click (second click)
  const handleIconClick = (menuSetter, isOpen) => {
    if (isOpen) {
      menuSetter(false);
    } else {
      menuSetter(true);
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLangMenuOpen(false);
  };

  useEffect(() => {
    let timer;
    if (menuOpen) {
      timer = setTimeout(() => {
        setMenuOpen(false);
      }, 60000); // 1 minute
    }
    return () => clearTimeout(timer);
  }, [menuOpen]);

  const handleNavLinkClick = () => {
    setMenuOpen(false);
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <nav className="navbar">
        <div className="navbar-left">
          <LanguageSelector
            currentLanguage={i18n.language}
            onChangeLanguage={lng => {
              i18n.changeLanguage(lng);
              setLangMenuOpen(false);
            }}
          />
          <button id='notification-button'  className="notification-button" onClick={() => {handleNotificationClick(); handleIconClick(setMenuOpen, menuOpen);}} title={t('enable_notifications')}>
            <FaBell />
          </button>
          <button id='register-button' className="register-button" onClick={() => {navigate('/Register'); handleIconClick(setMenuOpen, menuOpen);}} title="Register">
            <FaUserPlus />
          </button>
        </div>
<div className={`nav-links ${menuOpen ? 'open' : ''}`}>
          <NavLink to="/" className="nav-item" activeClassName="active" end onClick={handleNavLinkClick}>
            <FaHome />
            <span className="nav-text">{t('Dashboard')}</span>
          </NavLink>
          <NavLink to="/about" className="nav-item" activeClassName="active" onClick={handleNavLinkClick}>
            <FaInfoCircle />
            <span className="nav-text">{t('about')}</span>
          </NavLink>
          <NavLink to="/pricing" className="nav-item" activeClassName="active" onClick={handleNavLinkClick}>
            <FaDollarSign />
            <span className="nav-text">{t('pricing')}</span>
          </NavLink>
          <NavLink to="/Monetize" className="nav-item" activeClassName="active" onClick={handleNavLinkClick}>
            <img src={coinsIcon} alt="Monetize" className="nav-icon" />
            <span className="nav-text">{t('Monetize')}</span>
          </NavLink>
          {!isMobile && user ? (
            <NavLink to="/profile" className="nav-item" activeClassName="active" onClick={handleNavLinkClick}>
              <FaUserCircle />
              <span className="nav-text">{t('profile')}</span>
            </NavLink>
          ) : (
            <NavLink to="/login" className="nav-item" activeClassName="active" onClick={handleNavLinkClick}>
              <FaUserCircle />
              <span className="nav-text">{t('login')}</span>
            </NavLink>
          )}
          {user && user.role === 'admin' ? (
            <NavLink to="/dashboard" className="nav-item" activeClassName="active" onClick={handleNavLinkClick}>
              <FaTachometerAlt />
              <span className="nav-text">{t('dashboard')}</span>
            </NavLink>
          ) : null}
        </div>
        <div className="navbar-right">
          <button id='mode-toggle-button' className="mode-toggle-button" onClick={() => {setDeveloperMode(!developerMode); navigate('/settings'); handleIconClick(setMenuOpen, menuOpen);}} title={`Switch to ${developerMode ? 'Basic' : 'Developer'} Mode`}>
            {developerMode ? <FaToggleOn /> : <FaToggleOff />}
          </button>
          <span className="mode-indicator">{developerMode ? "Developer" : "Basic"}</span>
          <div className="menu-icon" onClick={() => handleIconClick(setMenuOpen, menuOpen)} title={menuOpen ? "Close Menu" : "Open Menu"}>
            {menuOpen ? <FaTimes /> : <FaBars />}
          </div>
          <div className="menu-profile-container">
            {user || isMobile ? (
              <div
                className="profile-icon"
                title={user ? t('profile') : t('login')}
                onClick={() => {
                  if (user) {
                    if (window.location.pathname === '/profile') {
                      navigate('/');
                    } else {
                      navigate('/profile');
                    }
                  } else {
                    navigate('/login');
                  }
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (user) {
                    if (e.key === 'Enter' || e.key === ' ') {
                      if (window.location.pathname === '/profile') {
                        navigate('/');
                      } else {
                        navigate('/profile');
                      }
                    }
                  } else {
                    if (e.key === 'Enter' || e.key === ' ') {
                      navigate('/login');
                    }
                  }
                }}
              >
                {user && user.profile?.profileImage ? (
                  <img
                    src={`http://localhost:5000${user.profile.profileImage}`}
                    alt="Profile Avatar"
                    className="navbar-avatar"
                    crossOrigin="anonymous"
                    onError={(e) => {
                      if (!e.target.src.includes('data:image') && !e.target.src.includes('localhost') && !e.target.src.includes('blob:')) {
                        e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNFMEUwRTAiLz4KICA8Y2lyY2xlIGN4PSI1MCIgY3k9IjM1IiByPSIyMCIgZmlsbD0iI0ZGRiIvPgogIDxjaXJjbGUgY3g9IjUwIiBjeT0iNzUiIHI9IjI1IiBmaWxsPSIjQkJCIi8+Cjwvc3ZnPgo=";
                      }
                    }}
                  />
                ) : (
                  <FaUserCircle />
                )}
              </div>
            ) : (
              <div
                className="login-icon"
                title={t('login')}
                onClick={() => navigate('/login')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate('/login');
                  }
                }}
              >
                <FaUserCircle />
              </div>
            )}
          </div>
          {/* <div style={{ color: 'red', fontSize: '12px', marginLeft: '10px' }}>
            {userStatusText}
          </div> */}
        </div>
      </nav>
    </Suspense>
  );
};

export default Navbar;
